import { Suspense, lazy } from 'react';
import { AppLoader, SkeletonLoader } from 'components/loading';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, Outlet } from 'react-router-dom';
import MainLayout from 'layouts/MainLayout';
import ScrollToTop from 'components/ScrollToTop';
import ErrorBoundary from 'components/ErrorBoundary';
import AuthGuard from 'components/guards/AuthGuard';
import GuestGuard from 'components/guards/GuestGuard';
import SuperAdminGuard from 'components/guards/SuperAdminGuard';
import { useActivityTracker } from 'hooks/useActivityTracker';

// Immediate load components (critical for first load)
import Login from 'pages/login';
import NotFound from 'pages/NotFound';

// Lazy load components (loaded on demand)
const Dashboard = lazy(() => import('pages/dashboard'));
const Profile = lazy(() => import('pages/profile'));
const ActivityLog = lazy(() => import('pages/profile/ActivityLog'));
const EditProfile = lazy(() => import('pages/profile/EditProfile'));
const ChangePassword = lazy(() => import('pages/profile/ChangePassword'));
const OrganizationAdmin = lazy(() => import('pages/admin-organization'));
const EditOrganizationAdmin = lazy(() => import('pages/admin-organization/EditOrganization'));
const Organization = lazy(() => import('pages/organization'));
const EditOrganization = lazy(() => import('pages/organization/EditOrganization'));
const Accounts = lazy(() => import('pages/admin-accounts'));
const UserDetail = lazy(() => import('pages/admin-accounts/detail'));
const EditOrganizationInfo = lazy(() => import('pages/admin-accounts/EditOrganizationInfo'));
const EditPropertyAdmin = lazy(() => import('pages/admin-accounts/EditProperty'));
const CreatePropertyAdmin = lazy(() => import('pages/admin-accounts/CreateProperty'));
const CreateRoomTypeAdmin = lazy(() => import('pages/admin-accounts/room-type/CreateRoomType'));
const EditRoomTypeAdmin = lazy(() => import('pages/admin-accounts/room-type/EditRoomType'));
const CreateRoomAdmin = lazy(() => import('pages/admin-accounts/room/CreateRoom'));
const EditRoomAdmin = lazy(() => import('pages/admin-accounts/room/EditRoom'));
const AIAnalysisDashboard = lazy(() => import('pages/ai-analysis'));
const CreateAccount = lazy(() => import('pages/admin-accounts/CreateAccount'));
const EditAccount = lazy(() => import('pages/admin-accounts/EditAccount'));
const AdminLogs = lazy(() => import('pages/admin-logs'));
const AdminData = lazy(() => import('pages/admin-data'));
const AdminDashboard = lazy(() => import('pages/admin-dashboard'));
const AdminAIUsages = lazy(() => import('pages/admin-ai-usages'));
const AdminUserJourney = lazy(() => import('pages/admin-user-journey'));
const AdminUserJourneyDetail = lazy(() => import('pages/admin-user-journey/detail'));
const AdminTours = lazy(() => import('pages/admin-tours'));
const TourEditor = lazy(() => import('pages/admin-tours/TourEditor'));
const AdminMCP = lazy(() => import('pages/admin-mcp'));
const CreateProperty = lazy(() => import('pages/property/CreateProperty'));
const EditProperty = lazy(() => import('pages/property/EditProperty'));
const CreateRoomType = lazy(() => import('pages/room-type/CreateRoomType'));
const EditRoomType = lazy(() => import('pages/room-type/EditRoomType'));
const CreateRoom = lazy(() => import('pages/room/CreateRoom'));
const EditRoom = lazy(() => import('pages/room/EditRoom'));
const Reservation = lazy(() => import('pages/reservation'));
const Report = lazy(() => import('pages/report'));
const ReportLanding = lazy(() => import('pages/report/ReportLanding'));
const ReservationsReport = lazy(() => import('pages/report/ReservationsReport'));
const MarketSegmentReport = lazy(() => import('pages/report/MarketSegmentReport'));
const BookingChannelReport = lazy(() => import('pages/report/BookingChannelReport'));
const RoomTypeReport = lazy(() => import('pages/report/RoomTypeReport'));
const BookingWindowReport = lazy(() => import('pages/report/BookingWindowReport'));
const NationalityReport = lazy(() => import('pages/report/NationalityReport'));
const PerformanceReport = lazy(() => import('pages/report/PerformanceReport'));
const RevenueMovementReport = lazy(() => import('pages/report/RevenueMovementReport'));
const ComingSoon = lazy(() => import('pages/ComingSoon'));

const MarketInsightPage = lazy(() => import('pages/market-insight'));
const KPIForecastPage = lazy(() => import('pages/kpi-forecast/KPIForecastPage'));
const AnalyzeV2Playground = lazy(() => import('pages/playground/analyze-v2'));

const ActivityTracker = () => {
  useActivityTracker();
  return null;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ActivityTracker />
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public routes (accessible without authentication) */}
          <Route element={<GuestGuard />}>
            <Route path="/login" element={<Login />} />
          </Route>

          {/* Protected routes (require authentication) */}
          <Route element={<AuthGuard />}>
            <Route
              element={
                <MainLayout>
                  <Outlet />
                </MainLayout>
              }
            >
              <Route
                path="/dashboard"
                element={
                  <Suspense fallback={<SkeletonLoader type="dashboard" />}>
                    <Dashboard />
                  </Suspense>
                }
              />
              {/* Profile routes */}
              <Route
                path="/profile"
                element={
                  <Suspense fallback={<SkeletonLoader type="profile" />}>
                    <Profile />
                  </Suspense>
                }
              />
              <Route
                path="/profile/edit"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading profile editor..." />}>
                    <EditProfile />
                  </Suspense>
                }
              />
              <Route
                path="/profile/change-password"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading..." />}>
                    <ChangePassword />
                  </Suspense>
                }
              />
              <Route
                path="/profile/activity"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading activity logs..." />}>
                    <ActivityLog />
                  </Suspense>
                }
              />
              {/* Admin management routes */}
              <Route
                path="/organization-admin"
                element={
                  <Suspense fallback={<SkeletonLoader type="table" />}>
                    <OrganizationAdmin />
                  </Suspense>
                }
              />
              <Route
                path="/organization-admin/edit"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading editor..." />}>
                    <EditOrganizationAdmin />
                  </Suspense>
                }
              />
              {/* Organization routes */}
              <Route
                path="/organization"
                element={
                  <Suspense fallback={<SkeletonLoader type="card" count={3} />}>
                    <Organization />
                  </Suspense>
                }
              />
              <Route
                path="/organization/edit"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading organization editor..." />}>
                    <EditOrganization />
                  </Suspense>
                }
              />
              <Route
                path="/organization/:id/property/create"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading form..." />}>
                    <CreateProperty />
                  </Suspense>
                }
              />
              <Route
                path="/organization/:id/property/:propertyId/edit"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading property editor..." />}>
                    <EditProperty />
                  </Suspense>
                }
              />
              <Route
                path="/property/:propertyId/room-type/create"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading form..." />}>
                    <CreateRoomType />
                  </Suspense>
                }
              />
              <Route
                path="/property/:propertyId/room-type/:roomTypeId/edit"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading room type editor..." />}>
                    <EditRoomType />
                  </Suspense>
                }
              />
              <Route
                path="/property/:propertyId/room-type/:roomTypeId/room/create"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading form..." />}>
                    <CreateRoom />
                  </Suspense>
                }
              />
              <Route
                path="/property/:propertyId/room-type/:roomTypeId/room/:roomId/edit"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading room editor..." />}>
                    <EditRoom />
                  </Suspense>
                }
              />
              {/* Account management routes - Super Admin Only */}
              <Route element={<SuperAdminGuard />}>
                <Route
                  path="/admin/dashboard"
                  element={
                    <Suspense fallback={<SkeletonLoader type="dashboard" />}>
                      <AdminDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/ai-usage"
                  element={
                    <Suspense fallback={<SkeletonLoader type="table" />}>
                      <AdminAIUsages />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts"
                  element={
                    <Suspense fallback={<SkeletonLoader type="table" />}>
                      <Accounts />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/create"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading account form..." />}>
                      <CreateAccount />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/detail"
                  element={
                    <Suspense fallback={<SkeletonLoader type="profile" />}>
                      <UserDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/edit"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading account editor..." />}>
                      <EditAccount />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/organization/:organizationId/edit"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading organization editor..." />}>
                      <EditOrganizationInfo />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/organization/:organizationId/create-property"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading property creator..." />}>
                      <CreatePropertyAdmin />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/organization/:organizationId/edit-property/:propertyId"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading property editor..." />}>
                      <EditPropertyAdmin />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/property/:propertyId/room-type/create"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading room type creator..." />}>
                      <CreateRoomTypeAdmin />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/property/:propertyId/room-type/:roomTypeId/edit"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading room type editor..." />}>
                      <EditRoomTypeAdmin />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/property/:propertyId/room-type/:roomTypeId/room/create"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading room creator..." />}>
                      <CreateRoomAdmin />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/accounts/:id/property/:propertyId/room-type/:roomTypeId/room/:roomId/edit"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading room editor..." />}>
                      <EditRoomAdmin />
                    </Suspense>
                  }
                />
                {/* Admin Logs */}
                <Route
                  path="/admin/logs"
                  element={
                    <Suspense fallback={<SkeletonLoader type="table" />}>
                      <AdminLogs />
                    </Suspense>
                  }
                />
                {/* Admin Data */}
                <Route
                  path="/admin/data"
                  element={
                    <Suspense fallback={<SkeletonLoader type="table" />}>
                      <AdminData />
                    </Suspense>
                  }
                />
                {/* User Journey - Super Admin */}
                <Route
                  path="/admin/user-journey"
                  element={
                    <Suspense fallback={<SkeletonLoader type="table" />}>
                      <AdminUserJourney />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/user-journey/:userId"
                  element={
                    <Suspense fallback={<SkeletonLoader type="profile" />}>
                      <AdminUserJourneyDetail />
                    </Suspense>
                  }
                />
                {/* Product Tours */}
                <Route
                  path="/admin/tours"
                  element={
                    <Suspense fallback={<SkeletonLoader type="table" />}>
                      <AdminTours />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/tours/new"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading tour editor..." />}>
                      <TourEditor />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/tours/:id/edit"
                  element={
                    <Suspense fallback={<AppLoader variant="minimal" message="Loading tour editor..." />}>
                      <TourEditor />
                    </Suspense>
                  }
                />
                {/* MCP Settings */}
                <Route
                  path="/admin/mcp"
                  element={
                    <Suspense fallback={<SkeletonLoader type="table" />}>
                      <AdminMCP />
                    </Suspense>
                  }
                />
              </Route>
              {/* Reservation */}
              <Route
                path="/reservation"
                element={
                  <Suspense fallback={<SkeletonLoader type="table" />}>
                    <Reservation />
                  </Suspense>
                }
              />
              {/* Reports */}
              <Route
                path="/report"
                element={
                  <Suspense fallback={<SkeletonLoader type="dashboard" />}>
                    <ReportLanding />
                  </Suspense>
                }
              />
              <Route
                path="/report/dashboard"
                element={
                  <Suspense fallback={<SkeletonLoader type="dashboard" />}>
                    <Report />
                  </Suspense>
                }
              />
              <Route
                path="/report/reservations"
                element={
                  <Suspense fallback={<SkeletonLoader type="table" />}>
                    <ReservationsReport />
                  </Suspense>
                }
              />
              <Route
                path="/report/market-segment"
                element={
                  <Suspense fallback={<SkeletonLoader type="table" />}>
                    <MarketSegmentReport />
                  </Suspense>
                }
              />
              <Route
                path="/report/booking-channel"
                element={
                  <Suspense fallback={<SkeletonLoader type="table" />}>
                    <BookingChannelReport />
                  </Suspense>
                }
              />
              <Route
                path="/report/room-type"
                element={
                  <Suspense fallback={<SkeletonLoader type="table" />}>
                    <RoomTypeReport />
                  </Suspense>
                }
              />
              <Route
                path="/report/booking-window"
                element={
                  <Suspense fallback={<SkeletonLoader type="table" />}>
                    <BookingWindowReport />
                  </Suspense>
                }
              />
              <Route
                path="/report/nationality"
                element={
                  <Suspense fallback={<SkeletonLoader type="table" />}>
                    <NationalityReport />
                  </Suspense>
                }
              />
              <Route
                path="/report/performance"
                element={
                  <Suspense fallback={<SkeletonLoader type="dashboard" />}>
                    <PerformanceReport />
                  </Suspense>
                }
              />
              <Route
                path="/report/revenue-movement"
                element={
                  <Suspense fallback={<SkeletonLoader type="dashboard" />}>
                    <RevenueMovementReport />
                  </Suspense>
                }
              />
              {/* AI Analysis */}

              <Route
                path="/ai-analysis"
                element={
                  <Suspense fallback={<SkeletonLoader type="dashboard" />}>
                    <AIAnalysisDashboard />
                  </Suspense>
                }
              />
              {/* KPI Forecast */}
              <Route
                path="/kpi-forecast"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading KPI Forecast..." />}>
                    <KPIForecastPage />
                  </Suspense>
                }
              />
              <Route
                path="/market-insight"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading market insights..." />}>
                    <MarketInsightPage />
                  </Suspense>
                }
              />
              <Route
                path="/google-maps"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading market insights..." />}>
                    <MarketInsightPage />
                  </Suspense>
                }
              />
              {/* Playground - Development Only */}
              <Route
                path="/playground/analyze-v2"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading playground..." />}>
                    <AnalyzeV2Playground />
                  </Suspense>
                }
              />
              {/* Coming Soon */}
              <Route
                path="/coming-soon"
                element={
                  <Suspense fallback={<AppLoader variant="minimal" message="Loading..." />}>
                    <ComingSoon />
                  </Suspense>
                }
              />
              {/* Redirect root to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>

          {/* Fallback route - show 404 NotFound page */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
